/**
 * The in-season chapter's translation between the pinned client and the §8 components.
 *
 * Same discipline as chapter 0's own mapping module, and the same three labels on
 * every mapping:
 *
 *   TRANSFORM  a shape change that loses nothing
 *   DERIVE     a value computed from what the contract does carry, stated as such
 *   GAP        a field a component requires that the contract does not expose
 *
 * No GAP is filled with an invented measurement. A field the contract does not publish
 * renders as `null` with a reason key (`docs/adr/0014`), never as a zero — a zero on a
 * clearing share, a bias or a terminal position would be read as evidence.
 *
 * The two mappings worth reading before changing anything are at the bottom: gate D2's
 * verdict and gate D2's market rows. They are the only place in this chapter where a
 * three-valued fact could be flattened into a two-valued one, and `docs/adr/0052`
 * records why it must not be.
 */

import type {
  AccuracyRow,
  AlertSummary,
  GateD2EvidenceRow,
  GateState,
  LifecycleProjection,
  PriceActionSummary,
  ProposalDriver,
  ProposalSummary,
  TransferSummary,
  WriteBackState,
  WriteBackSummary,
} from '@/api/generated';
import { EXCEPTION_RULE_LABEL_KEY, measureLabelKey, sizeName, type Exception, type Lineage, type SizeName, type ThresholdClass } from '@/planning';

// ── exceptions ───────────────────────────────────────────────────────────────

/**
 * DERIVE — a free-text severity into the four-value threshold class §12.6 needs.
 *
 * An unrecognised severity shows as `serious`, and that is a decision rather than a
 * default: downgrading it hides a breach, dropping the alert hides it completely, and
 * calling everything critical trains a planner to ignore critical. The class is labelled
 * either way, so an alert the queue could not classify is visible as one.
 *
 * This repeats the table chapter 0 keeps in its own mapping module, because a cross-feature
 * import is a build failure by design (`CLAUDE.md` §2.2). The duplication is the visible
 * cost of the rule; promoting the mapping is a conversation, and it is in the report.
 */
const SEVERITY_CLASS: Readonly<Record<string, ThresholdClass>> = {
  good: 'good',
  info: 'good',
  low: 'warning',
  warning: 'warning',
  medium: 'serious',
  serious: 'serious',
  high: 'critical',
  critical: 'critical',
};

export const UNCLASSIFIED_SEVERITY: ThresholdClass = 'serious';

export function toThresholdClass(severity: string): ThresholdClass {
  return SEVERITY_CLASS[severity.trim().toLowerCase()] ?? UNCLASSIFIED_SEVERITY;
}

/**
 * DERIVE — a rule identifier into the catalogue key the queue renders.
 *
 * The rule ids the platform publishes today are the dotted ones (`inseason.cover_below`,
 * `inseason.size_run_broken`, audit 4: a table keyed on the older bare names left every one of a
 * production queue's 200 rows «Regola non riconosciuta»), so the exception rules' own catalogue
 * (`EXCEPTION_RULE_LABEL_KEY`, the table the Exceptions queue reads) is consulted after this one.
 * A rule neither names gets the `unclassified` key — see `exceptionType` for the better answer
 * when its measure is known.
 */
const RULE_KEY: Readonly<Record<string, string>> = {
  size_run_break: 'inseason.exception.sizeRunBreak',
  size_run_integrity: 'inseason.exception.sizeRunBreak',
  forecast_bias: 'inseason.exception.forecastBias',
  last_chase_date: 'inseason.exception.lastChaseDate',
  otb_overspend: 'inseason.exception.otbOverspend',
  open_to_buy_value: 'inseason.exception.otbOverspend',
  cover_breach: 'inseason.exception.coverBreach',
};

export const UNCLASSIFIED_RULE_KEY = 'foundations.rule.unclassified';

const own = (table: Readonly<Record<string, string>>, key: string): string | undefined =>
  Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;

export function toExceptionTypeKey(ruleId: string): string {
  return own(RULE_KEY, ruleId) ?? own(EXCEPTION_RULE_LABEL_KEY, ruleId) ?? UNCLASSIFIED_RULE_KEY;
}

/**
 * DERIVE — an alert's type in words: its rule's catalogue name, else the words of the measure the
 * rule watches (the platform's `exception-rules` register publishes the id, the measure and the
 * grain, no name), else «Regola non ancora descritta». The alert's own `measure` is that same field
 * of the rule. `measureWords` turns a measure code into its words; the id of an unnamed rule is
 * shown behind the «id» button by the detail (`ExceptionFacts`), never made into a name.
 */
export function exceptionType(
  alert: Pick<AlertSummary, 'ruleId' | 'measure'>,
  measureWords: (measure: string) => string = (measure) => measure,
): { readonly typeKey: string; readonly typeValues?: Readonly<Record<string, string>> } {
  const named = toExceptionTypeKey(alert.ruleId);
  if (named !== UNCLASSIFIED_RULE_KEY) return { typeKey: named };
  if (measureLabelKey(alert.measure) === null) return { typeKey: UNCLASSIFIED_RULE_KEY };
  return { typeKey: 'inseason.exception.ruleOnMeasure', typeValues: { measure: measureWords(alert.measure) } };
}

/** Whether the alert's rule is named by the catalogue — otherwise the detail offers its id. */
export function isRuleNamed(ruleId: string): boolean {
  return toExceptionTypeKey(ruleId) !== UNCLASSIFIED_RULE_KEY;
}

/**
 * GAP — `AlertSummary` carries no lineage.
 *
 * G2.2 requires every number to answer "where did this come from" with the measure
 * definition, the register version, the source batch and the plan version. The alert
 * model publishes the measure and nothing else, so the other four are EMPTY rather than
 * plausible: a blank batch id cannot be cited in a review and a fabricated one can.
 */
export function unsourcedLineage(measure: string): Lineage {
  return { measure, definition: '', registerVersion: '', sourceBatch: '', planVersionId: '' };
}

/**
 * An alert's lineage, from the fields `AlertSummary` now publishes.
 *
 * It used to be a GAP: the alert model carried the measure and nothing else, and this row said
 * so with four empty fields. The pinned client now publishes `definitionId`, `metricsVersion`,
 * `batchId` and `planVersionId` on every alert, and the platform's mapper sets all four — the
 * 2026-09-25 audit found the queue still blanking them. A field the platform sends as null
 * stays EMPTY rather than plausible: a blank batch id cannot be cited in a review and a
 * fabricated one can.
 */
export function alertLineage(alert: AlertSummary): Lineage {
  return {
    measure: alert.measure,
    definition: alert.definitionId ?? '',
    registerVersion: alert.metricsVersion ?? '',
    sourceBatch: alert.batchId ?? '',
    planVersionId: alert.planVersionId ?? '',
  };
}

/**
 * TRANSFORM + GAP — an alert into a queue row.
 *
 * `actions` is EMPTY. The pinned spec exposes no in-season disposition endpoint, so
 * there is no action this queue could offer that would persist, and a working-looking
 * Approve button that discards the decision on unmount is precisely what G2 §2.6
 * forbids. The workbench states the absence rather than rendering a dead control.
 */
export function toExceptions(
  alerts: readonly AlertSummary[],
  /**
   * How the row names its subject — the workbench passes the store and article by name (usability
   * audit B4); without it, the platform's `subjectId`.
   */
  labelOf: (alert: AlertSummary) => string = (alert) => alert.subjectId,
  /** A measure code in words (the rule's name when the catalogue has none); the code without it. */
  measureWords?: (measure: string) => string,
): readonly Exception[] {
  return alerts.map((alert) => ({
    id: alert.alertId,
    thresholdClass: toThresholdClass(alert.severity),
    ...exceptionType(alert, measureWords),
    entityId: alert.subjectId,
    entityLabel: labelOf(alert),
    measure: alert.measure,
    actual: { kind: 'number', value: alert.observed },
    expected: { kind: 'number', value: alert.threshold },
    detectedAt: alert.raisedAt,
    lineage: alertLineage(alert),
    actions: [],
  }));
}

// ── §4.2 proposals ───────────────────────────────────────────────────────────

export interface ProposalRow {
  readonly proposalId: string;
  readonly entityLabel: string;
  readonly mode: string;
  readonly decisionClass: string;
  readonly currentUnits: number;
  readonly requestedUnits: number;
  readonly expectedImpact: number;
  readonly currencyCode: string;
  readonly boundBy: string;
  readonly reasonCode: string;
  readonly drivers: readonly ProposalDriver[];
  readonly shipWeek: string;
}

/**
 * TRANSFORM — a published proposal into the row the workbench table renders.
 *
 * The drivers are carried through with their sign rather than flattened to bare codes, so
 * a planner can see which driver pushed the quantity up and which pulled it down.
 * `confidence` and `model_version` are **nullable and undefaulted**; they used to bar
 * `RecommendationCard`, which required both. Since bata-ui#148 3.3 the card takes `null`
 * and renders the absence, and `./explain.tsx` mounts it on live proposals (ADR 6005).
 *
 * `units` is what the engine PROPOSED and `requested_units` what the doors REQUESTED (the
 * platform's `ProposalRow` note — they were labelled the other way round, "current" and
 * "proposed", until bata-ui#148 3.3); both are published, so neither is derived. The field
 * name `currentUnits` is kept for the export's column key.
 */
export function toProposalRows(proposals: readonly ProposalSummary[]): readonly ProposalRow[] {
  return proposals.map((proposal) => ({
    proposalId: proposal.proposal_id,
    entityLabel: proposal.store_key,
    mode: proposal.mode,
    decisionClass: proposal.decision_class,
    currentUnits: proposal.units,
    requestedUnits: proposal.requested_units,
    expectedImpact: proposal.expected_impact,
    currencyCode: proposal.currency_code,
    boundBy: proposal.bound_by,
    reasonCode: proposal.reason_code,
    drivers: proposal.drivers ?? [],
    shipWeek: proposal.ship_week,
  }));
}

/**
 * The named constraints, and the reason codes that travel with a proposal.
 *
 * "Why this quantity and not more" is the first question a planner asks, and the answer
 * is one of a named set. The set is DATA (G5) — the platform can add one without a
 * front-end release — so a code this build does not name renders as a labelled
 * `unclassified` badge rather than as `⟦inseason.reason.…⟧` or as nothing at all. A code
 * silently dropped is a proposal whose binding constraint is invisible.
 *
 * ── TWO VOCABULARIES MEET IN THIS COLUMN, AND ONLY ONE WAS NAMED ────────────
 *
 * The map used to carry ten codes, of which the allocation engine emits **three**.
 * `planning.allocation.types.ENGINE_REASON_CODES` is eight codes and is explicitly
 * "part of the published contract — renaming one is a G7.5 MAJOR bump", so those are
 * what an engine-produced line actually carries. Five of them — `pres_min_floor`,
 * `capacity_capped`, `door_protected`, `reserve_carveout`, `withheld_for_chase` —
 * were absent here, and would have rendered as `unclassified` on the first real
 * proposal ever written. The graceful degradation was working exactly as designed and
 * would have hidden the gap behind a plausible badge.
 *
 * The other seven are the **replenishment** vocabulary — the pull layer's answer to
 * the same question, injected per request as `ReasonCodeVocabulary` because which
 * codes a country's planners may dispose against is a governed decision. They are kept
 * rather than replaced: both sets reach this column, from two producers, and a build
 * that named only one of them would be back where this note starts.
 */
const CONSTRAINT_KEY: Readonly<Record<string, string>> = {
  // The replenishment vocabulary — the pull layer's reasons.
  below_presentation_min: 'inseason.reason.below_presentation_min',
  cover_breach: 'inseason.reason.cover_breach',
  high_velocity_fill: 'inseason.reason.high_velocity_fill',
  capped_by_dc_stock: 'inseason.reason.capped_by_dc_stock',
  // ADR 6188 (platform): kept with fewer pairs — the extra pairs did not pay for their transport.
  capped_by_shipment_cost: 'inseason.reason.capped_by_shipment_cost',
  capped_by_capacity: 'inseason.reason.capped_by_capacity',
  position_not_reported: 'inseason.reason.position_not_reported',
  not_ranged: 'inseason.reason.not_ranged',
  // `ENGINE_REASON_CODES`, in the engine's own most-binding-first order. The three
  // below that need no entry of their own — `demand_rank`, `size_run_dropped`,
  // `case_rounded` — are spelled identically in both vocabularies.
  size_run_dropped: 'inseason.reason.size_run_dropped',
  reserve_carveout: 'inseason.reason.reserve_carveout',
  withheld_for_chase: 'inseason.reason.withheld_for_chase',
  door_protected: 'inseason.reason.door_protected',
  capacity_capped: 'inseason.reason.capacity_capped',
  case_rounded: 'inseason.reason.case_rounded',
  pres_min_floor: 'inseason.reason.pres_min_floor',
  demand_rank: 'inseason.reason.demand_rank',
};

export const UNCLASSIFIED_CONSTRAINT_KEY = 'inseason.reason.unclassified';

export function toConstraintKey(code: string): string {
  return CONSTRAINT_KEY[code] ?? UNCLASSIFIED_CONSTRAINT_KEY;
}

/** Mode is a parameter, not a fork (§4.2) — and a labelled one, not a raw token. */
const MODE_KEY: Readonly<Record<string, string>> = {
  pull: 'inseason.mode.pull',
  push: 'inseason.mode.push',
  replenish: 'inseason.mode.pull',
  allocate: 'inseason.mode.push',
};

export const UNCLASSIFIED_MODE_KEY = 'inseason.mode.unclassified';

export function toModeKey(mode: string): string {
  return MODE_KEY[mode] ?? UNCLASSIFIED_MODE_KEY;
}

/** The article and size a proposal names, kept as reference data rather than copy (G6). */
/**
 * A proposal's grain as one reference: store · article · size. The size is passed in as
 * words — `useSizeText()` over `sizeName(proposal)` — because naming it is a catalogue read
 * (an unnamed size says so in the planner's locale) and never the opaque `size_key`
 * (bata-platform#470 §1, `docs/adr/9027`).
 */
export function proposalGrain(
  proposal: ProposalSummary, size: string,
  /**
   * The store and the article as the screen names them (`run-names.tsx` `withCode`): «Santafé
   * (1203)» where the run's report names it. Omitted, the codes alone.
   */
  labels?: { readonly store: string; readonly article: string },
): string {
  return [labels?.store ?? proposal.store_key, labels?.article ?? proposal.article_key, size].join(' · ');
}

// ── §4.1 projections and accuracy ────────────────────────────────────────────

export interface ProjectionRow {
  readonly optionId: string;
  readonly clusterKey: string;
  readonly classification: string;
  readonly projectedSeasonUnits: number;
  readonly plannedSeasonUnits: number;
  readonly analogueCurveId: string;
  readonly censoredWeeks: number;
  readonly fitDistance: number;
}

/** TRANSFORM — the published projection, field for field. Nothing is derived here. */
export function toProjectionRows(
  projections: readonly LifecycleProjection[],
): readonly ProjectionRow[] {
  return projections.map((projection) => ({
    optionId: projection.option_id,
    clusterKey: projection.cluster_key,
    classification: projection.classification,
    projectedSeasonUnits: projection.projected_season_units,
    plannedSeasonUnits: projection.planned_season_units,
    analogueCurveId: projection.analogue_curve_id,
    censoredWeeks: projection.censored_weeks,
    fitDistance: projection.fit_distance,
  }));
}

/** The classification vocabulary is DATA (G5). An unlisted class is labelled, not dropped. */
const CLASSIFICATION_KEY: Readonly<Record<string, string>> = {
  winner: 'inseason.class.winner',
  on_plan: 'inseason.class.onPlan',
  loser: 'inseason.class.loser',
  dead: 'inseason.class.dead',
};

export const UNCLASSIFIED_CLASS_KEY = 'inseason.class.unclassified';

export function toClassificationKey(classification: string): string {
  return CLASSIFICATION_KEY[classification] ?? UNCLASSIFIED_CLASS_KEY;
}

export interface AccuracyView {
  readonly dimensionLabel: string;
  readonly hasEvidence: boolean;
  readonly observations: number;
  /** `null` where the warehouse published no figure — never a zero. */
  readonly wape: number | null;
  readonly bias: number | null;
  readonly trackingSignal: number | null;
}

/**
 * TRANSFORM — the accuracy warehouse's rows.
 *
 * `AccuracyRow` publishes every measure as nullable beside a `hasEvidence` flag, and
 * that pairing is the whole point: "no error" and "nothing sold" are different findings,
 * and a cluster with no actuals in the window must not render as a perfect forecast.
 * The nulls are carried through as nulls and the screen renders them with a reason key.
 *
 * `dimensionKey` is an ordered list of reference keys, joined for display. That is data
 * formatting, not a sentence — there is no translated fragment on either side of the
 * separator, so G6 test *b*'s concatenation rule is not in play.
 */
export function toAccuracyViews(rows: readonly AccuracyRow[]): readonly AccuracyView[] {
  return rows.map((row) => ({
    dimensionLabel: row.dimensionKey.join(' · '),
    hasEvidence: row.hasEvidence,
    observations: row.observations,
    wape: row.weightedAbsolutePercentageError ?? null,
    bias: row.bias ?? null,
    trackingSignal: row.trackingSignal ?? null,
  }));
}

/** A group the warehouse measured a bias for: it has evidence AND published the figure. One definition: the chart, the card and the table's filter. */
export function isBiasMeasured(row: AccuracyView): boolean {
  return row.hasEvidence && row.bias !== null;
}

/**
 * DERIVE — the subset a bias chart may plot.
 *
 * A series point needs a number. A group with no evidence has none, so it is excluded
 * from the chart and counted on the screen instead; plotting it at zero would draw the
 * unmeasured groups as the best-forecast ones.
 */
export function measuredBias(
  rows: readonly AccuracyView[],
): readonly { readonly label: string; readonly bias: number }[] {
  return rows
    .filter(isBiasMeasured)
    .map((row) => ({ label: row.dimensionLabel, bias: row.bias as number }));
}

/**
 * DERIVE — what the bias chart's title states, from the rows it plots (DEC-110: interpolate the
 * values read, never a sentence written in advance). Bias is the platform's signed error as a
 * share of actual — positive is over-forecast (bata-platform `planning.mlplatform.evaluation`
 * `bias`). `medianPercent` is `null` with nothing plotted. Pure.
 */
export function biasFinding(plotted: readonly { readonly bias: number }[]): {
  readonly groups: number;
  readonly over: number;
  readonly under: number;
  readonly medianPercent: number | null;
} {
  const sorted = plotted.map((row) => row.bias).sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const med = sorted.length === 0
    ? null
    : sorted.length % 2 === 1 ? (sorted[middle] as number) : ((sorted[middle - 1] as number) + (sorted[middle] as number)) / 2;
  return {
    groups: sorted.length,
    over: sorted.filter((value) => value > 0).length,
    under: sorted.filter((value) => value < 0).length,
    medianPercent: med === null ? null : med * 100,
  };
}

// ── §4.4 price actions ───────────────────────────────────────────────────────

export interface PriceActionRow {
  readonly priceActionId: string;
  readonly articleKey: string;
  readonly clusterKey: string;
  readonly channelKey: string;
  readonly effectiveWeek: string;
  readonly fromPrice: number;
  readonly toPrice: number;
  readonly depthFromFullPrice: number;
  readonly priceStream: string;
  readonly currencyCode: string;
  readonly expectedMarginRecovery: number;
  readonly terminalUnitsProjected: number;
  readonly terminalTargetUnits: number;
}

/**
 * TRANSFORM — the published price actions.
 *
 * A price *path* has one entry per remaining week; a price *action* is a change. The
 * planner approves actions and the pricing system receives actions, so the wired surface
 * is the action list. The path itself is solved by `POST /inseason/markdown/optimise`
 * from a payload of nineteen caller-supplied parameters, none of which has a default
 * because G1 makes a tunable in source a defect — see `docs/adr/0053`.
 */
export function toPriceActionRows(
  actions: readonly PriceActionSummary[],
): readonly PriceActionRow[] {
  return actions.map((action) => ({
    priceActionId: action.price_action_id,
    articleKey: action.article_key,
    clusterKey: action.cluster_key,
    channelKey: action.channel_key,
    effectiveWeek: action.effective_week,
    fromPrice: action.from_price,
    toPrice: action.to_price,
    depthFromFullPrice: action.depth_from_full_price,
    priceStream: action.price_stream,
    currencyCode: action.currency_code,
    expectedMarginRecovery: action.expected_margin_recovery,
    terminalUnitsProjected: action.terminal_units_projected,
    terminalTargetUnits: action.terminal_target_units,
  }));
}

/** The price-stream vocabulary is data. An unlisted stream is labelled, not recoloured. */
const STREAM_KEY: Readonly<Record<string, string>> = {
  regular: 'inseason.stream.regular',
  promotional: 'inseason.stream.promotional',
  clearance: 'inseason.stream.clearance',
};

export const UNCLASSIFIED_STREAM_KEY = 'inseason.stream.unclassified';

export function toStreamKey(stream: string): string {
  return STREAM_KEY[stream] ?? UNCLASSIFIED_STREAM_KEY;
}

// ── §4.5 transfers ───────────────────────────────────────────────────────────

export interface TransferRow {
  readonly transferId: string;
  readonly donorStoreKey: string;
  readonly recipientStoreKey: string;
  readonly articleKey: string;
  /** The size's join id — never shown; `size` is what a planner reads (`docs/adr/9027`). */
  readonly sizeKey: string;
  readonly size: SizeName;
  readonly units: number;
  readonly purpose: string;
  readonly laneCost: number;
  readonly runDamage: number;
  readonly netBenefit: number;
  readonly currencyCode: string;
  /**
   * The recorded disposition (ADR 6031), or `null` while no planner has decided it -
   * pending, never a default decision. `approve` is a recorded decision and not a transfer
   * that was sent: no warehouse command is raised for it.
   */
  readonly disposition: string | null;
  readonly dispositionReasonCode: string | null;
  readonly dispositionDecidedAt: string | null;
  /**
   * Who took the first decision: the verified principal and its profile name (bata-platform
   * ADR 9021). `null` while pending or superseded, and on a platform before v0.64. Optional so a
   * row built by hand (a test, a fixture) need not name one.
   */
  readonly dispositionActor?: string | null;
  readonly dispositionActorName?: string | null;
  /** Why the actor has no name (ADR 2127); `null` where a name stands. */
  readonly dispositionActorNameAbsentReason?: string | null;
  /**
   * The quantity an `adjust` set (bata-platform ADR 6052, owner ruling D-6); `null` on every
   * other decision, and on a platform before it.
   */
  readonly adjustedUnits: number | null;
  /**
   * `superseded` (bata-platform ADR 6048, DEC-05): a newer run of the market took over the
   * undecided line — which run, and when. `null` otherwise, and on a platform before v0.51.
   */
  readonly supersededByRunId: string | null;
  readonly supersededAt: string | null;
}

/** TRANSFORM — the published transfer proposals, with the economics that justified each. */
export function toTransferRows(transfers: readonly TransferSummary[]): readonly TransferRow[] {
  return transfers.map((transfer) => ({
    transferId: transfer.transfer_id,
    donorStoreKey: transfer.donor_store_key,
    recipientStoreKey: transfer.recipient_store_key,
    articleKey: transfer.article_key,
    sizeKey: transfer.size_key,
    size: sizeName(transfer),
    units: transfer.units,
    purpose: transfer.purpose,
    laneCost: transfer.lane_cost,
    runDamage: transfer.run_damage,
    netBenefit: transfer.net_benefit,
    currencyCode: transfer.currency_code,
    disposition: transfer.disposition ?? null,
    dispositionReasonCode: transfer.disposition_reason_code ?? null,
    dispositionDecidedAt: transfer.disposition_decided_at ?? null,
    dispositionActor: transfer.disposition_actor ?? null,
    dispositionActorName: transfer.disposition_actor_name ?? null,
    dispositionActorNameAbsentReason: transfer.disposition_actor_name_absent_reason ?? null,
    adjustedUnits: transfer.adjusted_units ?? null,
    supersededByRunId: transfer.superseded_by_run_id ?? null,
    supersededAt: transfer.superseded_at ?? null,
  }));
}

const PURPOSE_KEY: Readonly<Record<string, string>> = {
  rebalance: 'inseason.purpose.rebalance',
  run_repair: 'inseason.purpose.runRepair',
  consolidate: 'inseason.purpose.consolidate',
};

export const UNCLASSIFIED_PURPOSE_KEY = 'inseason.purpose.unclassified';

export function toPurposeKey(purpose: string): string {
  return PURPOSE_KEY[purpose] ?? UNCLASSIFIED_PURPOSE_KEY;
}

/**
 * The purposes this build names, in the order the screen presents them.
 *
 * `TransferSummary.purpose` is a bare `string` in the pinned spec — no enum, no
 * `$ref` — so the vocabulary is DATA (G5) and the platform can add a purpose without a
 * front-end release. These three are what this build has labels for, and the order is
 * the order a planner works: move stock that is in the wrong place, repair the runs the
 * moving broke, then consolidate what is left. Derived from `PURPOSE_KEY`'s own key
 * order rather than written out again, so a fourth label cannot be added without also
 * being ordered.
 */
export const NAMED_PURPOSES: readonly string[] = Object.keys(PURPOSE_KEY);

/** Whether this build has a label for the code, as opposed to a labelled fallback. */
export function isNamedPurpose(purpose: string): boolean {
  return Object.hasOwn(PURPOSE_KEY, purpose);
}

// ── §14 §5 O5 write-back ─────────────────────────────────────────────────────

/**
 * The seven delivery states, each with its own label.
 *
 * There is deliberately no mapping that folds two of them together. §14 test E10:
 * *"No UI, API response or report may render `dispatched`, `delivered` or `unknown` as
 * 'sent' or 'done'."* A state the build does not recognise is labelled `unrecognised`
 * rather than being defaulted into one of the seven — defaulting it into `delivered`
 * would be the E10 violation arriving through the back door.
 */
const DELIVERY_STATE_KEY: Readonly<Record<string, string>> = {
  approved: 'inseason.delivery.approved',
  dispatched: 'inseason.delivery.dispatched',
  delivered: 'inseason.delivery.delivered',
  acknowledged: 'inseason.delivery.acknowledged',
  executed: 'inseason.delivery.executed',
  rejected: 'inseason.delivery.rejected',
  unknown: 'inseason.delivery.unknown',
  // bata-platform ADR 6105: two states a planner reports. Partly executed is NOT done.
  partially_executed: 'inseason.delivery.partially_executed',
  cancelled: 'inseason.delivery.cancelled',
};

export const UNRECOGNISED_DELIVERY_KEY = 'inseason.delivery.unrecognised';

export function toDeliveryStateKey(state: string): string {
  return DELIVERY_STATE_KEY[state] ?? UNRECOGNISED_DELIVERY_KEY;
}

/**
 * Executed as the target reported it. Only `executed` is that; nothing else counts as it,
 * here or anywhere else in this chapter.
 */
export function isExecuted(command: WriteBackState): boolean {
  return command.state === 'executed';
}

/**
 * A planner confirmed it executed in Oracle (`acknowledged`, ADR 6037). Owner ruling R-16
 * (bata-ui#166 W4-8): counted, and counted **separately** from `executed` — a person's
 * confirmation and a target's report are two facts, and neither is added to the other.
 */
export function isAcknowledged(command: WriteBackState): boolean {
  return command.state === 'acknowledged';
}

/** In transit is not sent and is not done. Two states, named, and counted as themselves. */
export function isInTransit(command: WriteBackState): boolean {
  return command.state === 'dispatched' || command.state === 'delivered';
}

/**
 * `unknown` is a real operational state with an owner, and so is a rejection.
 *
 * **`sla_elapsed` is three-valued and is compared against `true` explicitly.** The
 * platform widened it to `boolean | null` so that a command whose target has no
 * configured SLA answers `null` rather than `false` — "nobody set a deadline" and "the
 * deadline has not passed" are different facts, and no SLA is configured anywhere in
 * this estate today, so `false` would have reported every command as within time while
 * nothing was being measured.
 *
 * A truthiness test would read `null` as "not elapsed", which is the same collapse
 * arriving by a different route: right by accident today, and silently wrong on the day
 * a target reports one. So an absent deadline raises no attention here, and it does not
 * claim the command is on time either — that distinction belongs to whatever renders
 * the value, not to this predicate.
 */
export function needsAttention(command: WriteBackState): boolean {
  return (
    command.state === 'rejected' || command.state === 'unknown' || command.sla_elapsed === true
  );
}

/**
 * The write-back log's four counts. From the platform's own sums when it publishes them
 * (`GET /inseason/write-back/summary`, ADR 6170 — the log added up in the store, over every command the
 * caller may read) and, where it does not, from the rows the page has walked. The same predicates both
 * ways: `executed` is only `executed`, a planner's `acknowledged` is its own count (R-16), in transit is
 * `dispatched` or `delivered`, and attention is `rejected`, `unknown` or past its deadline.
 */
export interface WriteBackCounts {
  readonly commands: number;
  readonly executed: number;
  readonly acknowledged: number;
  readonly inTransit: number;
  readonly attention: number;
}

/** The counts of the rows in hand. */
export function writeBackCounts(rows: readonly WriteBackState[]): WriteBackCounts {
  return {
    commands: rows.length,
    executed: rows.filter(isExecuted).length,
    acknowledged: rows.filter(isAcknowledged).length,
    inTransit: rows.filter(isInTransit).length,
    attention: rows.filter(needsAttention).length,
  };
}

/** The counts as the store adds them up: `by_state` names only the states present, so absent is none. */
export function writeBackCountsOfSummary(summary: WriteBackSummary): WriteBackCounts {
  const of = (state: string): number => summary.by_state[state] ?? 0;
  return {
    commands: summary.commands,
    executed: of('executed'),
    acknowledged: of('acknowledged'),
    inTransit: of('dispatched') + of('delivered'),
    attention: summary.needs_attention,
  };
}

// ── gate D2: the three states that must not collapse ─────────────────────────

/** reqs/08 §8's identifier for the cost-to-serve gate. */
export const D2_GATE_ID = 'D2';

/**
 * The gate's verdict, as three values and not two.
 *
 * `GateState.met` is nullable *on purpose*, and the generated type says so: *"`met` is
 * deliberately nullable and is null whenever `evidence_available` is false."* The
 * platform holds it null while any market is unmeasured so that a pass cannot be earned
 * by the markets that were left out.
 *
 * So there are three verdicts and they are three different things:
 *
 *   `met`               the gate cleared on the evidence that exists
 *   `not-met`           the gate was measured and did not clear
 *   `not-yet-measured`  the gate has no verdict yet
 *
 * Collapsing the third into the second understates a passing programme; collapsing it
 * into the first — a green tick on an unmeasured gate — misreports the gate outright,
 * and reqs/08 §8 attaches a *failure action* to D2 ("hold transfers in that market")
 * that an operator would then not take. `docs/adr/0052`.
 *
 * Nothing here computes `met`. The verdict is read from the field; `evidenceAvailable`
 * is reported separately and never substituted for it.
 */
export type GateVerdict = 'met' | 'not-met' | 'not-yet-measured';

export interface D2GateView {
  /** False where the gate register publishes no D2 row at all — itself a finding. */
  readonly present: boolean;
  /** Measurability, as published. A separate fact from the verdict. */
  readonly evidenceAvailable: boolean;
  readonly verdict: GateVerdict;
  /** reqs/08 §8's wording, verbatim, as a catalogue key from the platform's register. */
  readonly failureActionKey: string | null;
  readonly decidedOn: string | null;
  readonly waveId: string | null;
}

export const ABSENT_D2_GATE: D2GateView = {
  present: false,
  evidenceAvailable: false,
  verdict: 'not-yet-measured',
  failureActionKey: null,
  decidedOn: null,
  waveId: null,
};

export function toD2GateView(gates: readonly GateState[]): D2GateView {
  const gate = gates.find((row) => row.gateId === D2_GATE_ID);
  if (gate === undefined) return ABSENT_D2_GATE;
  // `met === true` is the ONLY path to `met`. `undefined` and `null` are the same
  // statement — no verdict — and both take the third branch.
  const verdict: GateVerdict = gate.met === true
    ? 'met'
    : gate.met === false ? 'not-met' : 'not-yet-measured';
  return {
    present: true,
    evidenceAvailable: gate.evidenceAvailable,
    verdict,
    failureActionKey: gate.failureActionKey,
    decidedOn: gate.decidedOn ?? null,
    waveId: gate.waveId,
  };
}

/**
 * A market is measured or it is not, and that is not the same axis as enabled or held.
 *
 * `list_d2_evidence` returns uncalibrated rows rather than filtering them, because "they
 * are the finding — *D2 was not measurable in this market*", and dropping them "would
 * leave a caller unable to tell no improvement from no evidence". So measurability is a
 * column of its own beside the enablement column, and the uncalibrated lane count is
 * shown as the number that makes it true.
 */
export type Measurability = 'measured' | 'unmeasured';
export type TransferEnablement = 'enabled' | 'held';

export interface D2MarketView {
  readonly countryKey: string;
  readonly measurability: Measurability;
  readonly uncalibratedLanes: number;
  readonly candidateLanes: number;
  readonly clearingLanes: number;
  /**
   * As published. Kept visible on an unmeasured market, with the row flagged. `null` where the
   * signed definition makes it undefined — no lane could be scored (bata-platform ADR 6050).
   */
  readonly clearingShare: number | null;
  readonly minimumClearingShare: number;
  /** `null` for the same markets: a median over no scored lane. Never 0. */
  readonly medianNetBenefit: number | null;
  /**
   * Why `clearingShare` or `medianNetBenefit` is absent, as a catalogue key; `null` when both
   * are published.
   */
  readonly measuresAbsentKey: string | null;
  readonly enablement: TransferEnablement;
  /** The reason a market is held, as a catalogue key. Held rows always carry one. */
  readonly holdReasonKey: string | null;
  readonly assessedOn: string;
  readonly calibrationVersionId: string;
}

/**
 * Hold reason codes are data (G5). An unlisted code is labelled, never swallowed.
 *
 * Keyed on the codes the platform actually writes: `planning.transfer.enablement`'s
 * `HOLD_REASONS` (bata-platform v0.61.0 `packages/planning/transfer/enablement.py:55-57`),
 * which the D2 assessment row, the rebalance summary and the outbox event all carry verbatim.
 */
const HOLD_REASON_KEY: Readonly<Record<string, string>> = {
  'transfer.below_clearing_threshold': 'inseason.holdReason.belowMinimumClearingShare',
  'transfer.lane_costs_uncalibrated': 'inseason.holdReason.lanesUncalibrated',
  'transfer.not_assessed': 'inseason.holdReason.notAssessed',
};

export const UNCLASSIFIED_HOLD_REASON_KEY = 'inseason.holdReason.unclassified';

/**
 * A market held with no reason code published still says *held* and says the code is
 * missing. Silence is not a reason, and an operator told "transfers are off here" is
 * owed either the code or the fact that there isn't one.
 */
export const UNSTATED_HOLD_REASON_KEY = 'inseason.holdReason.unstated';

export function toHoldReasonKey(code: string | null | undefined): string {
  if (code === null || code === undefined || code.trim() === '') {
    return UNSTATED_HOLD_REASON_KEY;
  }
  return HOLD_REASON_KEY[code] ?? UNCLASSIFIED_HOLD_REASON_KEY;
}

/**
 * Why a market's clearing share and median net benefit are absent (bata-platform ADR 6050). A
 * code this build does not know still reads as absent, labelled as unclassified — never as 0.
 */
const MEASURES_ABSENT_KEY: Readonly<Record<string, string>> = {
  'planning.inseason.cost_to_serve.no_scored_lane': 'inseason.measuresAbsent.noScoredLane',
};

export const UNSTATED_MEASURES_ABSENT_KEY = 'inseason.measuresAbsent.unstated';
export const UNCLASSIFIED_MEASURES_ABSENT_KEY = 'inseason.measuresAbsent.unclassified';

export function toMeasuresAbsentKey(code: string | null | undefined): string {
  if (code === null || code === undefined || code.trim() === '') return UNSTATED_MEASURES_ABSENT_KEY;
  return MEASURES_ABSENT_KEY[code] ?? UNCLASSIFIED_MEASURES_ABSENT_KEY;
}

/**
 * One view per market: the market's newest assessment.
 *
 * The dated read (`GET /inseason/d2-evidence?as_of=…`) answers every assessment on or before
 * `as_of`, a market's history rather than its state, ordered by market and then newest first
 * (`recorded_at` desc, then `assessed_on`, then `assessment_id`; bata-platform ADR 6033). Mapped
 * row for row, a daily cycle read as one market per day: production showed CO five times and
 * counted "5 markets". The first row of each market is the one in force at `as_of`.
 */
export function toD2MarketViews(
  rows: readonly GateD2EvidenceRow[],
): readonly D2MarketView[] {
  const seen = new Set<string>();
  const newest = rows.filter((row) => {
    if (seen.has(row.country_key)) return false;
    seen.add(row.country_key);
    return true;
  });
  return newest.map((row) => ({
    countryKey: row.country_key,
    measurability: row.uncalibrated_lanes > 0 ? 'unmeasured' : 'measured',
    uncalibratedLanes: row.uncalibrated_lanes,
    candidateLanes: row.candidate_lanes,
    clearingLanes: row.clearing_lanes,
    clearingShare: row.clearing_share,
    minimumClearingShare: row.minimum_clearing_share,
    medianNetBenefit: row.median_net_benefit,
    measuresAbsentKey: row.clearing_share === null || row.median_net_benefit === null
      ? toMeasuresAbsentKey(row.measures_absent_reason)
      : null,
    enablement: row.transfers_enabled ? 'enabled' : 'held',
    holdReasonKey: row.transfers_enabled ? null : toHoldReasonKey(row.hold_reason_code),
    assessedOn: row.assessed_on,
    calibrationVersionId: row.calibration_version_id,
  }));
}

/** How many markets are held. A count of held markets, not a count of failures. */
export function heldMarkets(markets: readonly D2MarketView[]): number {
  return markets.filter((market) => market.enablement === 'held').length;
}

/** How many markets D2 could not be measured in. Reported, never folded into the above. */
export function unmeasuredMarkets(markets: readonly D2MarketView[]): number {
  return markets.filter((market) => market.measurability === 'unmeasured').length;
}
